import { useEffect, useState } from "react";
import { Plus, FileText, Eye, Ban } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ReceiptPreviewDialog } from "@/components/ReceiptPreviewDialog";
import { RECEIPTS_TAB } from "@/constants/testIds";
import { toast } from "sonner";

export function ReceiptsTab({ employeeId }) {
  const [receipts, setReceipts] = useState([]);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ period_label: "", issue_date: "", note: "" });
  const [file, setFile] = useState(null);
  const [previewReceipt, setPreviewReceipt] = useState(null);
  const [invalidateTarget, setInvalidateTarget] = useState(null);
  const [invalidateReason, setInvalidateReason] = useState("");
  const [invalidating, setInvalidating] = useState(false);

  const load = () => {
    api.get(`/employees/${employeeId}/receipts`).then(({ data }) => setReceipts(data));
  };

  useEffect(load, [employeeId]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("period_label", form.period_label);
      formData.append("issue_date", form.issue_date);
      if (form.note) formData.append("note", form.note);
      formData.append("file", file);
      const { data } = await api.post(`/employees/${employeeId}/receipts`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.message);
      setOpen(false);
      setForm({ period_label: "", issue_date: "", note: "" });
      setFile(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleInvalidate = async () => {
    if (!invalidateTarget) return;
    setInvalidating(true);
    try {
      const { data } = await api.post(`/employees/${employeeId}/receipts/${invalidateTarget.id}/invalidate`, {
        reason: invalidateReason || null,
      });
      toast.success(data.message);
      setInvalidateTarget(null);
      setInvalidateReason("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setInvalidating(false);
    }
  };

  return (
    <div className="space-y-3 mt-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={RECEIPTS_TAB.uploadButton} size="sm" className="bg-terracotta hover:bg-terracotta/90 rounded-full">
              <Plus className="w-4 h-4 mr-1" /> Subir recibo
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-bone">
            <DialogHeader><DialogTitle>Subir recibo de nómina</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Periodo</Label>
                <Input data-testid={RECEIPTS_TAB.periodInput} value={form.period_label} onChange={(e) => setForm((f) => ({ ...f, period_label: e.target.value }))} placeholder="Ej. Enero 2026 (1a quincena)" />
              </div>
              <div>
                <Label>Fecha de emisión</Label>
                <Input data-testid={RECEIPTS_TAB.issueDateInput} type="date" value={form.issue_date} onChange={(e) => setForm((f) => ({ ...f, issue_date: e.target.value }))} />
              </div>
              <div>
                <Label>Nota (opcional)</Label>
                <Input data-testid={RECEIPTS_TAB.noteInput} value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
              </div>
              <div>
                <Label>Archivo (PDF, JPG o PNG, máx. 10 MB)</Label>
                <Input data-testid={RECEIPTS_TAB.fileInput} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setFile(e.target.files?.[0] || null)} />
              </div>
              <Button
                data-testid={RECEIPTS_TAB.submitButton}
                disabled={!form.period_label || !form.issue_date || !file || submitting}
                onClick={handleSubmit}
                className="w-full bg-petroleum rounded-full"
              >
                {submitting ? "Subiendo..." : "Guardar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {receipts.map((r) => (
        <div key={r.id} data-testid={RECEIPTS_TAB.historyRow} className={`bg-card border border-petroleum/10 rounded-xl p-4 flex items-center justify-between ${r.invalidated ? "opacity-60" : ""}`}>
          <div className="flex items-center gap-3">
            <FileText className="w-4 h-4 text-petroleum shrink-0" strokeWidth={1.5} />
            <div>
              <p className="text-sm font-medium text-charcoal">
                {r.period_label}
                {r.invalidated && (
                  <span data-testid={RECEIPTS_TAB.invalidatedBadge} className="ml-2 text-xs font-normal text-terracotta bg-terracotta/10 rounded-full px-2 py-0.5">
                    Invalidado{r.invalidated_by ? ` por ${r.invalidated_by}` : ""}
                  </span>
                )}
              </p>
              <p className="text-xs text-charcoal/50">
                {r.issue_date}{r.note ? ` · ${r.note}` : ""}
                {r.invalidated && r.invalidation_reason ? ` · Motivo: ${r.invalidation_reason}` : ""}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            {r.has_file ? (
              <Button data-testid={RECEIPTS_TAB.previewButton} size="sm" variant="ghost" onClick={() => setPreviewReceipt(r)} className="text-petroleum hover:text-petroleum/80">
                <Eye className="w-4 h-4 mr-1" /> Vista previa
              </Button>
            ) : (
              <span className="text-xs text-charcoal/40">Sin archivo</span>
            )}
            {!r.invalidated && (
              <Button
                data-testid={RECEIPTS_TAB.invalidateButton}
                size="sm"
                variant="ghost"
                onClick={() => setInvalidateTarget(r)}
                className="text-charcoal/50 hover:text-terracotta"
              >
                <Ban className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
      {receipts.length === 0 && <p className="text-charcoal/50 text-sm">Sin recibos registrados.</p>}

      <Dialog open={!!invalidateTarget} onOpenChange={(v) => { if (!v) { setInvalidateTarget(null); setInvalidateReason(""); } }}>
        <DialogContent className="bg-bone">
          <DialogHeader>
            <DialogTitle>Invalidar recibo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-charcoal/70">
              "{invalidateTarget?.period_label}" dejará de aparecer para el empleado, pero se conserva en el historial de auditoría (quién lo subió y quién lo invalidó). El archivo no se borra.
            </p>
            <div>
              <Label>Motivo (opcional)</Label>
              <Input
                data-testid={RECEIPTS_TAB.invalidateReasonInput}
                value={invalidateReason}
                onChange={(e) => setInvalidateReason(e.target.value)}
                placeholder="Ej. Se subió por error, periodo duplicado"
              />
            </div>
            <Button
              data-testid={RECEIPTS_TAB.invalidateConfirmButton}
              disabled={invalidating}
              onClick={handleInvalidate}
              className="w-full bg-terracotta hover:bg-terracotta/90 rounded-full"
            >
              {invalidating ? "Invalidando..." : "Invalidar recibo"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ReceiptPreviewDialog
        employeeId={employeeId}
        receipt={previewReceipt}
        open={!!previewReceipt}
        onOpenChange={(v) => !v && setPreviewReceipt(null)}
      />
    </div>
  );
}

